/**
 * DeliveryPODReviewPage — `/delivery/pod/:id` staff review of a captured POD.
 *
 * Wires `GET /v1/pods/:id` and `POST /v1/pods/:id/review`. The capture flow
 * itself lives in mobile-driver (Phase 4). When no `:id` is in the URL, this
 * lists the review queue (`GET /v1/pods/queue`).
 */
import { type JSX, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { CheckCircle2, XCircle } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../../lib/api/client';
import { getPod, listPodQueue, reviewPod } from '../../lib/api/delivery';
import { DeferredNotice, PageHeader, StatusBadge } from '../shared/StaffUI';

export function DeliveryPODReviewPage(): JSX.Element {
  const { id = '' } = useParams<{ id?: string }>();

  if (id === '') {
    return <PodReviewQueue />;
  }
  return <PodReviewDetail id={id} />;
}

function PodReviewQueue(): JSX.Element {
  const queue = useQuery({
    queryKey: ['delivery', 'pods', 'queue', { reviewStatus: 'PENDING' }],
    queryFn: () => listPodQueue({ pageSize: 25, reviewStatus: 'PENDING' }),
    retry: false,
    staleTime: 15_000,
  });
  const denied =
    queue.isError && queue.error instanceof ApiError && (queue.error.status === 401 || queue.error.status === 403);

  return (
    <div className="space-y-6">
      <PageHeader
        title="POD Review Queue"
        description="Proof-of-delivery captures awaiting reviewer sign-off."
      />
      {queue.isPending ? (
        <p className="text-sm text-muted">Loading…</p>
      ) : denied ? (
        <DeferredNotice title="Permission required">
          The dev token doesn&apos;t carry <code className="font-mono">Delivery/reviewPod</code>.
        </DeferredNotice>
      ) : queue.isError ? (
        <DeferredNotice title="Service unreachable">{(queue.error as Error).message}</DeferredNotice>
      ) : (
        <div className="bg-white border border-border rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-muted">
              <tr>
                <th className="text-left px-4 py-2">POD</th>
                <th className="text-left px-4 py-2">Mail item</th>
                <th className="text-left px-4 py-2">Driver</th>
                <th className="text-left px-4 py-2">Recipient</th>
                <th className="text-left px-4 py-2">Captured</th>
                <th className="text-right px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {(queue.data?.items ?? []).map((p) => (
                <tr key={p.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2 font-mono text-xs text-gray-900">{p.id.slice(0, 8)}…</td>
                  <td className="px-4 py-2 text-xs text-muted">{p.mailItemId}</td>
                  <td className="px-4 py-2 text-xs text-muted">{p.driverId}</td>
                  <td className="px-4 py-2 text-xs text-gray-900">{p.recipientName}</td>
                  <td className="px-4 py-2 text-xs text-muted">{new Date(p.capturedAt).toLocaleString()}</td>
                  <td className="px-4 py-2 text-right">
                    <Link to={`/delivery/pod/${encodeURIComponent(p.id)}`} className="text-xs font-semibold text-nipost-green hover:underline">
                      Review →
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {(queue.data?.items.length ?? 0) === 0 ? (
            <p className="p-8 text-center text-sm text-muted">No PODs awaiting review.</p>
          ) : null}
        </div>
      )}
    </div>
  );
}

function PodReviewDetail({ id }: { readonly id: string }): JSX.Element {
  const qc = useQueryClient();
  const [notes, setNotes] = useState<string>('');
  const [outcome, setOutcome] = useState<{ kind: 'ok' | 'err'; message: string } | null>(null);

  const pod = useQuery({
    queryKey: ['delivery', 'pods', 'detail', id],
    queryFn: () => getPod(id),
    retry: false,
  });

  const reviewMutation = useMutation({
    mutationFn: (decision: 'APPROVE' | 'REJECT') => reviewPod(id, decision, notes !== '' ? notes : undefined),
    onSuccess: (rec) => {
      setOutcome({ kind: 'ok', message: `POD ${rec.reviewStatus.toLowerCase()}` });
      void qc.invalidateQueries({ queryKey: ['delivery', 'pods'] });
    },
    onError: (err) => {
      setOutcome({ kind: 'err', message: err instanceof Error ? err.message : 'Review failed' });
    },
  });

  const denied =
    pod.isError && pod.error instanceof ApiError && (pod.error.status === 401 || pod.error.status === 403);

  return (
    <div className="space-y-6 max-w-3xl">
      <PageHeader
        title="POD review"
        description="Approve or reject a captured proof of delivery."
        actions={
          <Link to="/delivery/pod" className="px-3 py-2 text-sm font-medium text-nipost-green bg-nipost-green-light rounded-lg hover:bg-nipost-green hover:text-white">
            Back to queue
          </Link>
        }
      />
      {pod.isPending ? (
        <p className="text-sm text-muted">Loading…</p>
      ) : denied ? (
        <DeferredNotice title="Permission required">
          The dev token doesn&apos;t carry <code className="font-mono">Delivery/readPod</code>.
        </DeferredNotice>
      ) : pod.isError ? (
        <DeferredNotice title="Service unreachable">{(pod.error as Error).message}</DeferredNotice>
      ) : pod.data ? (
        <>
          <div className="bg-white border border-border rounded-xl p-5 space-y-3 text-sm">
            <div className="flex items-center justify-between">
              <p className="font-mono text-xs text-gray-900">{pod.data.id}</p>
              <StatusBadge status={pod.data.reviewStatus} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
              <div>
                <p className="text-muted">Mail item</p>
                <p className="font-mono text-gray-900 mt-0.5">{pod.data.mailItemId}</p>
              </div>
              <div>
                <p className="text-muted">Driver</p>
                <p className="font-mono text-gray-900 mt-0.5">{pod.data.driverId}</p>
              </div>
              <div>
                <p className="text-muted">Recipient</p>
                <p className="text-gray-900 mt-0.5">{pod.data.recipientName}</p>
              </div>
              <div>
                <p className="text-muted">Captured at</p>
                <p className="text-gray-900 mt-0.5">{new Date(pod.data.capturedAt).toLocaleString()}</p>
              </div>
              <div className="sm:col-span-2">
                <p className="text-muted">GPS</p>
                <p className="text-gray-900 mt-0.5">
                  {pod.data.latitude && pod.data.longitude ? `${pod.data.latitude}, ${pod.data.longitude}` : '—'}
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-muted mb-1">Signature</p>
                <div className="bg-gray-50 border border-border rounded-lg h-32 flex items-center justify-center text-xs text-muted">
                  ref: <code className="ml-1 font-mono">{pod.data.signatureRef.slice(0, 24)}…</code>
                </div>
              </div>
              <div>
                <p className="text-xs text-muted mb-1">Photo</p>
                <div className="bg-gray-50 border border-border rounded-lg h-32 flex items-center justify-center text-xs text-muted">
                  ref: <code className="ml-1 font-mono">{pod.data.photoRef.slice(0, 24)}…</code>
                </div>
              </div>
            </div>
            {pod.data.notes ? (
              <div>
                <p className="text-xs text-muted">Driver notes</p>
                <p className="text-xs text-gray-700 mt-1 italic">{pod.data.notes}</p>
              </div>
            ) : null}
          </div>

          <div className="bg-white border border-border rounded-xl p-5 space-y-3">
            <label className="block text-xs font-medium text-muted">Review notes (optional)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Add notes…"
              className="w-full px-3 py-2 text-sm border border-border rounded-lg"
            />
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => reviewMutation.mutate('APPROVE')}
                disabled={reviewMutation.isPending || pod.data.reviewStatus !== 'PENDING'}
                className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white text-sm font-semibold rounded-lg hover:bg-emerald-700 disabled:opacity-50"
              >
                <CheckCircle2 className="w-4 h-4" /> Approve
              </button>
              <button
                type="button"
                onClick={() => reviewMutation.mutate('REJECT')}
                disabled={reviewMutation.isPending || pod.data.reviewStatus !== 'PENDING'}
                className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white text-sm font-semibold rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                <XCircle className="w-4 h-4" /> Reject
              </button>
            </div>
            {outcome ? (
              <p className={outcome.kind === 'ok' ? 'text-xs text-emerald-700' : 'text-xs text-red-700'}>
                {outcome.message}
              </p>
            ) : null}
          </div>
        </>
      ) : null}
    </div>
  );
}
